import cl from 'classnames'
import {
  CurrencySelector,
  H2,
  ImageLabelLine,
  ImageLabelLineProps,
  Input,
  MultiSelect,
  ObjectOption,
  ObjectSelectSection,
  PageHeader,
  SectionContainer,
  TablePeriodFilter,
  Text1,
  VerticalLine
} from 'components/shared'
import { Select } from 'components/shared/Select'
import {
  AgreementDTO,
  AgreementItemDTO,
  AgreementItemPropertyTypes,
  AgreementItemTypes,
  AgreementPropertiesType,
  AgreementScopeDTO,
  AgreementStatusTypes,
  FilterTypes
} from 'models'
import React, { useCallback, useEffect, useState } from 'react'
import { OnSelectCallbackParam } from 'react-daterange-picker'
import { inject } from 'mobx-react'
import { GENERAL_STORE } from 'store'
import { Api, useGetIsResponsiveScreen } from '../../../utilities'
import styles from '../AgreementPage/AgreementPage.module.scss'
import { AgreementPageHeaderSecondLine } from '../AgreementPageHeaderSecondLine'
import { AddAgreementPageProps } from '../AddAgreementPage/AddAgreementPage.types'
import { useGetStores } from './hooks/useGetStores'
import { useGetProjects } from './hooks/useGetProjects'
import { useGetProducts } from './hooks/useGetProducts'
import settings from '../../../config/settings'

interface AgreementState {
  name: string
  budget: number
  budgetBuffer: number
  agreementStatus: AgreementStatusTypes
  items: {
    [key in AgreementItemTypes]?: AgreementPropertiesType
  }
}

const ShopifyAddAgreementPageComponent : React.FC<AddAgreementPageProps> = (props) => {
  const {
    [GENERAL_STORE]: { showSuccess, setLoadingOn, setLoadingOff, showError }
  } = props

  const searchParams = new URLSearchParams(window.location.search)
  const token = searchParams.get('token')
  const { isResponsive: isLargeMobile } = useGetIsResponsiveScreen('(max-width: 500px)')

  // Agreement state
  const [agreement, setAgreement] = useState<AgreementState>({
    name: '',
    budget: 0,
    budgetBuffer: 0,
    agreementStatus: AgreementStatusTypes.DRAFT_STORE,
    items: {
      [AgreementItemTypes.BLOB_SETTINGS]: {},
      [AgreementItemTypes.PRODUCT]: {},
      [AgreementItemTypes.VIDEO_COMMERCIAL]: {},
      [AgreementItemTypes.POSTER]: {},
      [AgreementItemTypes.PRODUCT_PURCHASE]: {}
    }
  })

  // Period state
  const [period, setPeriod] = useState<OnSelectCallbackParam>(null)

  // Scope state
  const [scopeStoreId, setScopeStoreId] = useState<number>(0)
  const [scopeProjectIds, setScopeProjectIds] = useState<number[]>([])
  const [scopeProductIds, setScopeProductIds] = useState<ObjectOption[]>([])

  // Computed values
  const [isAgreementReadyToSave, setIsAgreementReadyToSave] = useState<boolean>(false)

  const { stores: storeOptions, isLoading: isLoadingStores } = useGetStores(token)
  const {
    projects: projectOptions,
    projectsRawData,
    isLoading: isLoadingProjects
  } = useGetProjects(scopeStoreId, token)
  const {
    products: productOptions,
    productsRawData,
    storeData,
    isLoading: isLoadingProducts
  } = useGetProducts(scopeStoreId, token)

  // Functions
  const setAgreementProps = useCallback((props: Partial<AgreementState>) => {
    setAgreement(prev => ({ ...prev, ...props }))
  }, [])

  const getItemProp = useCallback((
    itemType: AgreementItemTypes,
    propType: AgreementItemPropertyTypes
  ): number => {
    return agreement.items[itemType]?.[propType] || 0
  }, [agreement.items])

  const setAgreementItemProp = useCallback((
    itemType: AgreementItemTypes,
    propType: AgreementItemPropertyTypes
  ) => {
    return (value: number) => {
      setAgreement(prev => ({
        ...prev,
        items: {
          ...prev.items,
          [itemType]: {
            ...prev.items[itemType],
            [propType]: value
          }
        }
      }))
    }
  }, [])

  const onPeriodChange = useCallback((newPeriod: OnSelectCallbackParam) => {
    setPeriod(newPeriod)
  }, [])

  const getChangeByKeyId = useCallback((_keyId: string) => {
    // Empty function - returns undefined for version change
    return undefined
  }, [])

  // Transform state to AgreementDTO format
  const transformToAgreementDTO = useCallback((): Partial<AgreementDTO> => {
    // Transform items
    const items: AgreementItemDTO[] = Object.entries(agreement.items)
      .filter(([_, properties]) => Object.keys(properties).length > 0)
      .map(([type, properties]) => ({
        id: null,
        type: type as AgreementItemTypes,
        properties: properties as AgreementPropertiesType
      }))

    // Transform period to startDate/endDate
    const startDate = period?.start ? period.start.valueOf() : null
    const endDate = period?.end ? period.end.valueOf() : null

    // Transform scopes with full data
    const scopes: AgreementScopeDTO[] = scopeStoreId ? [{
      id: null,
      budget: null,
      name: 'Scope 1',
      filters: [
        // Brands filter - full store/brand data
        {
          id: null,
          budget: null,
          type: FilterTypes.BRANDS,
          brands: storeData ? [storeData as any] : [],
          projects: null,
          products: null,
          values: null
        },
        // Projects filter - full project data
        {
          id: null,
          budget: null,
          type: FilterTypes.PROJECTS,
          brands: null,
          projects: projectsRawData.filter(p => scopeProjectIds.includes(p.id)) as any,
          products: null,
          values: null
        },
        // Products filter - full product data
        {
          id: null,
          budget: null,
          type: FilterTypes.PRODUCTS,
          brands: null,
          projects: null,
          products: productsRawData.filter(p => scopeProductIds.some(sp => sp.id === p.id)) as any,
          values: null
        }
      ]
    }] : []

    return {
      name: agreement.name,
      budget: agreement.budget,
      budgetBuffer: agreement.budgetBuffer,
      budgetRemaining: null,
      budgetSpent: null,
      createdDate: null,
      effectOnElements: false,
      endDate: endDate,
      externalId: null,
      id: null,
      images: [],
      info: '',
      items: items,
      loadContents: null,
      mainImage: null,
      organizationId: null,
      ownerId: null,
      relatedMedia: [],
      scopes,
      startDate: startDate,
      status: 'NEW' as any,
      agreementStatus: agreement.agreementStatus,
      tags: null
    }
  }, [agreement, period, scopeStoreId, scopeProjectIds, scopeProductIds, storeData, projectsRawData, productsRawData])

  const onSubmit = useCallback(async () => {
    if (!isAgreementReadyToSave) return

    setLoadingOn()

    try {
      const agreementDTO = transformToAgreementDTO()

      // Call create API
      const createResponse = await Api.post<AgreementDTO>(
        '/agreement/create',
        agreementDTO,
        'marketPlaceApi'
        , {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        }
      )

      console.log('Agreement created successfully:', createResponse)

      // Call update API to activate the agreement
      await Api.post<AgreementDTO>(
        '/agreement/update',
        {
          ...createResponse,
          budgetBuffer: agreementDTO.budgetBuffer,
          items: agreementDTO.items,
          scopes: agreementDTO.scopes,
          status: AgreementStatusTypes.ACTIVE,
          agreementStatus: AgreementStatusTypes.AGREED
        },
        'marketPlaceApi'
        , {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        }
      )

      console.log('Agreement updated successfully')

      setLoadingOff()

      const baseUrl = settings.marketPlaceSite
      window.location.href = `${baseUrl}/settings?status=success_agreement_created`
    } catch (error) {
      console.error('Error creating agreement:', error)
      setLoadingOff()
      showError('Error creating agreement. Please try again.')
    }
  }, [isAgreementReadyToSave, transformToAgreementDTO, token, setLoadingOn, setLoadingOff, showSuccess, showError])

  useEffect(() => {
    // Update isAgreementReadyToSave based on agreement state
    const hasName = agreement.name.trim().length > 0
    const hasBudget = agreement.budget > 0
    const hasPeriod = period !== null
    setIsAgreementReadyToSave(hasName && hasBudget && hasPeriod)
  }, [agreement.name, agreement.budget, period])

  if (!token) return null

  return (
    <div className={cl(styles.pageContainer, styles.scrollable)}>
      <PageHeader
        navSegments={[
          {
            label: 'Shopify Add Agreement'
          }
        ]}
        middleContainerChildren={(
          <>
            <CurrencySelector
              style={{ marginLeft: 'auto' }}
            />
            <VerticalLine
              style={{ margin: '0 5px 0 35px' }}
            />
          </>
        )}
        buttons={[{
          children: 'Save',
          preset: 'primary',
          onClick: onSubmit,
          disabled: !isAgreementReadyToSave
        }]}
        secondLineContainerProps={{
          children: <AgreementPageHeaderSecondLine />
        }}
      />

      <SectionContainer className={styles.pageSection} extraPaddingBottom>
        <H2>Agreement details</H2>

        <SectionContainer preset='secondary' className={styles.pageSection}>
          <Input
            label='Name'
            value={agreement.name}
            onChange={name => setAgreementProps({ name })}
            versionChange={getChangeByKeyId('name')}
          />

          <div className={cl(styles.flexContainer, styles.marginTop)}>
            <Input
              label='Budget'
              type='number'
              unitsText='USD'
              min={0}
              value={agreement.budget}
              onChangeNumber={budget => setAgreementProps({ budget })}
              versionChange={getChangeByKeyId('budget')}
            />
            <Input
              label='Buffer'
              type='number'
              unitsText='%'
              min={0}
              value={agreement.budgetBuffer}
              onChangeNumber={budgetBuffer => setAgreementProps({ budgetBuffer })}
              versionChange={getChangeByKeyId('budgetBuffer')}
            />
            <div className={styles.periodPickerContainer}>
              <Text1 extraBold style={{ marginBottom: 6 }}>Period</Text1>
              <TablePeriodFilter
                placeholder='Pick start and end date'
                period={period}
                setPeriod={onPeriodChange}
                className={styles.periodPicker}
                versionChange={getChangeByKeyId('period')}
              />
            </div>
          </div>
        </SectionContainer>
      </SectionContainer>

      <SectionContainer className={styles.pageSection} extraPaddingBottom>
        <H2>Configuration</H2>
        <SectionContainer preset='secondary' className={styles.pageSection}>
          <Text1 extraBold>BLOB</Text1>
          <div className={cl(styles.flexContainer, styles.marginTop)}>
            <div className={styles.flexContainer}>
              <Input
                label='Price per one trigger'
                unitsText='USD'
                type='number'
                min={0}
                value={getItemProp(
                  AgreementItemTypes.BLOB_SETTINGS,
                  AgreementItemPropertyTypes.BLOB_TRIGGERED
                )}
                onChangeNumber={setAgreementItemProp(
                  AgreementItemTypes.BLOB_SETTINGS,
                  AgreementItemPropertyTypes.BLOB_TRIGGERED
                )}
                versionChange={getChangeByKeyId(AgreementItemPropertyTypes.BLOB_TRIGGERED)}
              />
              <Input
                label='Max attach time'
                unitsText='seconds'
                type='number'
                min={0}
                value={getItemProp(
                  AgreementItemTypes.BLOB_SETTINGS,
                  AgreementItemPropertyTypes.MAX_ATTACH_TIME_SECONDS
                )}
                onChangeNumber={setAgreementItemProp(
                  AgreementItemTypes.BLOB_SETTINGS,
                  AgreementItemPropertyTypes.MAX_ATTACH_TIME_SECONDS
                )}
                versionChange={getChangeByKeyId(AgreementItemPropertyTypes.MAX_ATTACH_TIME_SECONDS)}
              />
            </div>
            <div>
              <Text1 extraBold style={{ marginBottom: 6 }}> {/* Remove horizontalFillLine */}
              Price per time on screen
              </Text1>
              <div className={styles.flexContainer}>
                <Input
                  label='Attached'
                  {...(!isLargeMobile && {
                    labelPosition: 'left'
                  })}
                  labelProps={{ bold: false, style: { color: styles.secondaryContent, width: 75, paddingRight: 35 } }}
                  unitsText='USD'
                  type='number'
                  min={0}
                  value={getItemProp(
                    AgreementItemTypes.BLOB_SETTINGS,
                    AgreementItemPropertyTypes.PRICE_PER_TIME_ON_SCREEN_ATTACHED
                  )}
                  onChangeNumber={setAgreementItemProp(
                    AgreementItemTypes.BLOB_SETTINGS,
                    AgreementItemPropertyTypes.PRICE_PER_TIME_ON_SCREEN_ATTACHED
                  )}
                  versionChange={getChangeByKeyId(AgreementItemPropertyTypes.PRICE_PER_TIME_ON_SCREEN_ATTACHED)}
                />
                <Input
                  label='Detached'
                  {...(!isLargeMobile && {
                    labelPosition: 'left'
                  })}
                  labelProps={{ bold: false, style: { color: styles.secondaryContent, width: 75, paddingRight: 35 } }}
                  unitsText='USD'
                  type='number'
                  min={0}
                  value={getItemProp(
                    AgreementItemTypes.BLOB_SETTINGS,
                    AgreementItemPropertyTypes.PRICE_PER_TIME_ON_SCREEN_DETACHED
                  )}
                  onChangeNumber={setAgreementItemProp(
                    AgreementItemTypes.BLOB_SETTINGS,
                    AgreementItemPropertyTypes.PRICE_PER_TIME_ON_SCREEN_DETACHED
                  )}
                  versionChange={getChangeByKeyId(AgreementItemPropertyTypes.PRICE_PER_TIME_ON_SCREEN_DETACHED)}
                />
              </div>
            </div>
            <div>
              <Text1 extraBold style={{ marginBottom: 6 }} > {/* Remove horizontalFillLine */}
              Time in stack
              </Text1>
              <div className={styles.flexContainer}>
                <Input
                  label='Min'
                  {...(!isLargeMobile && {
                    labelPosition: 'left'
                  })}
                  labelProps={{ bold: false, style: { color: styles.secondaryContent, width: 75, paddingRight: 35 } }}
                  unitsText='seconds'
                  type='number'
                  min={0}
                  value={getItemProp(
                    AgreementItemTypes.BLOB_SETTINGS,
                    AgreementItemPropertyTypes.MINIMUM_TIME_IN_STACK
                  )}
                  onChangeNumber={setAgreementItemProp(
                    AgreementItemTypes.BLOB_SETTINGS,
                    AgreementItemPropertyTypes.MINIMUM_TIME_IN_STACK
                  )}
                  versionChange={getChangeByKeyId(AgreementItemPropertyTypes.MINIMUM_TIME_IN_STACK)}
                />
                <Input
                  label='Max'
                  {...(!isLargeMobile && {
                    labelPosition: 'left'
                  })}
                  labelProps={{ bold: false, style: { color: styles.secondaryContent, width: 75, paddingRight: 35 } }}
                  unitsText='seconds'
                  type='number'
                  min={0}
                  value={getItemProp(
                    AgreementItemTypes.BLOB_SETTINGS,
                    AgreementItemPropertyTypes.MAXIMUM_TIME_IN_STACK
                  )}
                  onChangeNumber={setAgreementItemProp(
                    AgreementItemTypes.BLOB_SETTINGS,
                    AgreementItemPropertyTypes.MAXIMUM_TIME_IN_STACK
                  )}
                  versionChange={getChangeByKeyId(AgreementItemPropertyTypes.MAXIMUM_TIME_IN_STACK)}
                />
              </div>
            </div>
          </div>
        </SectionContainer>

        <div className={cl(styles.pageSection, styles.flexContainer)}>
          <SectionContainer preset='secondary' style={{ flex: 2 }}>
            <Text1 extraBold>PRODUCT</Text1>
            <div className={cl(styles.flexContainer, styles.marginTop)}>
              <Input
                label='Price per opened product page'
                {...(!isLargeMobile && {
                  labelPosition: 'left'
                })}
                labelProps={{ style: { lineHeight: '17px', width: 150, paddingRight: 70 } }}
                unitsText='USD'
                type='number'
                min={0}
                value={getItemProp(
                  AgreementItemTypes.PRODUCT,
                  AgreementItemPropertyTypes.PRODUCT_PAGE_OPENED_FROM_BLOB
                )}
                onChangeNumber={setAgreementItemProp(
                  AgreementItemTypes.PRODUCT,
                  AgreementItemPropertyTypes.PRODUCT_PAGE_OPENED_FROM_BLOB
                )}
                versionChange={getChangeByKeyId(AgreementItemPropertyTypes.PRODUCT_PAGE_OPENED_FROM_BLOB)}
              />
              <Input
                label='Price per product added to cart'
                {...(!isLargeMobile && {
                  labelPosition: 'left'
                })}
                labelProps={{ style: { lineHeight: '17px', width: 150, paddingRight: 70 } }}
                unitsText='USD'
                type='number'
                min={0}
                value={getItemProp(
                  AgreementItemTypes.PRODUCT,
                  AgreementItemPropertyTypes.PRODUCT_ADDED_TO_CART
                )}
                onChangeNumber={setAgreementItemProp(
                  AgreementItemTypes.PRODUCT,
                  AgreementItemPropertyTypes.PRODUCT_ADDED_TO_CART
                )}
                versionChange={getChangeByKeyId(AgreementItemPropertyTypes.PRODUCT_ADDED_TO_CART)}
              />
            </div>

          </SectionContainer>
          <SectionContainer preset='secondary'>
            <Text1 extraBold>VIDEO COMMERCIAL</Text1>
            <Input
              label='Price per video playback'
              {...(!isLargeMobile && {
                labelPosition: 'left'
              })}
              labelProps={{ style: { lineHeight: '17px', width: 150, paddingRight: 70 } }}
              containerProps={{ className: styles.marginTop }}
              unitsText='USD'
              type='number'
              min={0}
              value={getItemProp(
                AgreementItemTypes.VIDEO_COMMERCIAL,
                AgreementItemPropertyTypes.PRICE_PER_VIDEO_PLAYBACK
              )}
              onChangeNumber={setAgreementItemProp(
                AgreementItemTypes.VIDEO_COMMERCIAL,
                AgreementItemPropertyTypes.PRICE_PER_VIDEO_PLAYBACK
              )}
              versionChange={getChangeByKeyId(AgreementItemPropertyTypes.PRICE_PER_VIDEO_PLAYBACK)}
            />
          </SectionContainer>
        </div>

        <div className={cl(styles.pageSection, styles.flexContainer)}>
          <SectionContainer preset='secondary' style={{ flex: 2 }}>
            <Text1 extraBold>POSTER</Text1>
            <div className={cl(styles.flexContainer, styles.marginTop)}>
              <Input
                label='Price per click'
                {...(!isLargeMobile && {
                  labelPosition: 'left'
                })}
                labelProps={{ style: { lineHeight: '17px', width: 150, paddingRight: 70 } }}
                unitsText='USD'
                type='number'
                min={0}
                value={getItemProp(
                  AgreementItemTypes.POSTER,
                  AgreementItemPropertyTypes.PRICE_PER_CLICK
                )}
                onChangeNumber={setAgreementItemProp(
                  AgreementItemTypes.POSTER,
                  AgreementItemPropertyTypes.PRICE_PER_CLICK
                )}
                versionChange={getChangeByKeyId(AgreementItemPropertyTypes.PRICE_PER_CLICK)}
              />
              <Input
                label='Price per second on screen'
                {...(!isLargeMobile && {
                  labelPosition: 'left'
                })}
                labelProps={{ style: { lineHeight: '17px', width: 150, paddingRight: 70 } }}
                unitsText='USD'
                type='number'
                min={0}
                value={getItemProp(
                  AgreementItemTypes.POSTER,
                  AgreementItemPropertyTypes.PRICE_PER_TIME_ON_SCREEN
                )}
                onChangeNumber={setAgreementItemProp(
                  AgreementItemTypes.POSTER,
                  AgreementItemPropertyTypes.PRICE_PER_TIME_ON_SCREEN
                )}
                versionChange={getChangeByKeyId(AgreementItemPropertyTypes.PRICE_PER_TIME_ON_SCREEN)}
              />
            </div>

          </SectionContainer>
          <SectionContainer preset='secondary'>
            <Text1 extraBold>PRODUCT PURCHASE</Text1>
            <Input
              label='Purchase fee'
              {...(!isLargeMobile && {
                labelPosition: 'left'
              })}
              labelProps={{ style: { lineHeight: '17px', width: 150, paddingRight: 70 } }}
              containerProps={{ className: styles.marginTop }}
              unitsText='%'
              type='number'
              min={0}
              value={getItemProp(
                AgreementItemTypes.PRODUCT_PURCHASE,
                AgreementItemPropertyTypes.PURCHASE_FEE
              )}
              onChangeNumber={setAgreementItemProp(
                AgreementItemTypes.PRODUCT_PURCHASE,
                AgreementItemPropertyTypes.PURCHASE_FEE
              )}
              versionChange={getChangeByKeyId(AgreementItemPropertyTypes.PURCHASE_FEE)}
            />
          </SectionContainer>
        </div>
      </SectionContainer>

      <SectionContainer className={styles.pageSection} extraPaddingBottom>
        {/* <H2>Agreement scope</H2> */}
        <SectionContainer preset='secondary' className={styles.pageSection}>
          <Text1>This agreement is valid for:</Text1>
          <Text1 bold style={{ marginTop: 16, marginBottom: 6 }}>Store</Text1>
          {isLoadingStores ? (
            <Text1>Loading stores...</Text1>
          ) : (
            <Select<number, ImageLabelLineProps>
              options={storeOptions}
              noNoneOption
              customOptionComponent={ImageLabelLine}
              onChange={(value) => setScopeStoreId(value)}
              value={scopeStoreId}
            />
          )}

          {Boolean(scopeStoreId) && (
            <>
              <Text1 bold style={{ marginTop: 16, marginBottom: 6 }}>Projects</Text1>
              {isLoadingProjects ? (
                <Text1>Loading projects...</Text1>
              ) : (
                <MultiSelect<number>
                  placeholder='Select project/s'
                  options={projectOptions}
                  value={scopeProjectIds}
                  onChange={(values) => setScopeProjectIds(values)}
                  closeMenuOnSelect={false}
                  style={ { width: '100%' } }
                />
              )}

              <Text1 bold style={{ marginTop: 16, marginBottom: 6 }}>Products</Text1>
              {isLoadingProducts ? (
                <Text1>Loading products...</Text1>
              ) : (
                <ObjectSelectSection
                  list={productOptions}
                  selected={scopeProductIds}
                  onSelectAllClick={() => {
                    setScopeProductIds(productOptions)
                  }}
                  onObjectClick={(id) => {
                    const product = productOptions.find(p => p.id === id)
                    if (!product) return

                    const isSelected = scopeProductIds.some(p => p.id === id)
                    if (isSelected) {
                      setScopeProductIds(scopeProductIds.filter(p => p.id !== id))
                    } else {
                      setScopeProductIds([...scopeProductIds, product])
                    }
                  }}
                />
              )}
            </>
          )}

        </SectionContainer>
      </SectionContainer>
    </div >
  )
}

const ShopifyAddAgreementPage = inject(GENERAL_STORE)(ShopifyAddAgreementPageComponent)
export { ShopifyAddAgreementPage }
